<template lang="pug">
.mail-record-page.flex.column.gap-20
  .desc 輸入收件者 Email 或選擇寄送時間區間後點選「篩選」，可查詢系統寄出的信件紀錄
  .filter.flex.align-end.gap-12.wrap
    .col.flex.column.align-start.gap-6
      .title.font-size-12 收件者 Email
      el-input(
        v-model="recipient"
        clearable
        placeholder="可留空，模糊比對"
        style="width: 220px"
        @keyup.enter.native="search")
    .col.flex.column.align-start.gap-6
      .title.font-size-12 寄送時間
      el-date-picker(
        v-model="times"
        type="daterange"
        range-separator="至"
        start-placeholder="開始日期"
        end-placeholder="結束日期"
        style="width: 360px")
    .col.flex.column.align-start.gap-6
      .title.font-size-12 信件類型
      el-select(
        v-model="emailTypes"
        multiple
        filterable
        collapse-tags
        clearable
        placeholder="不限"
        style="min-width: 200px")
        el-option(
          v-for="t in catalog.email_types"
          :key="t"
          :value="t"
          :label="emailTypeLabel(t)")
    .col.flex.column.align-start.gap-6
      .title.font-size-12 狀態
      el-select(
        v-model="statuses"
        multiple
        collapse-tags
        clearable
        placeholder="不限"
        style="min-width: 160px")
        el-option(
          v-for="s in catalog.statuses"
          :key="s"
          :value="s"
          :label="statusLabel(s)")
    .col.flex.align-center.gap-8
      el-button(type="primary", @click="search") 篩選
      el-button(@click="resetFilter") 重設
  template(v-if="logs.length > 0")
    .table.flex
      el-table(:data="logs", border, style="width: 100%")
        el-table-column(type="expand")
          template(slot-scope="scope")
            .detail.flex.column.gap-6
              .line 寄件者：{{ scope.row.from_email || '—' }}
              .line 關聯對象：{{ referenceText(scope.row) }}
              .line 實際寄出時間：{{ scope.row.sent_at ? $timestampToDateTimeMinute(scope.row.sent_at) : '—' }}
              .line 最後嘗試時間：{{ scope.row.last_attempted_at ? $timestampToDateTimeMinute(scope.row.last_attempted_at) : '—' }}
              .line 夾帶附件：{{ scope.row.has_attachment ? '是' : '否' }}
              .line(v-if="scope.row.skipped_reason") 略過原因：{{ scope.row.skipped_reason }}
              .line.danger-color(v-if="scope.row.error_type") 錯誤類型：{{ scope.row.error_type }}
              .line.danger-color(v-if="scope.row.error_message") 錯誤訊息：{{ scope.row.error_message }}
        el-table-column(label="寄送時間", width="160")
          template(slot-scope="scope")
            span {{ $timestampToDateTimeMinute(scope.row.created_at) }}
        el-table-column(label="收件者", min-width="200")
          template(slot-scope="scope")
            span {{ scope.row.recipient }}
        el-table-column(label="主旨", min-width="220")
          template(slot-scope="scope")
            span {{ scope.row.subject }}
        el-table-column(label="信件類型", min-width="180")
          template(slot-scope="scope")
            span {{ emailTypeLabel(scope.row.email_type) }}
        el-table-column(label="狀態", width="110")
          template(slot-scope="scope")
            span(:class="statusClass(scope.row.status)") {{ statusLabel(scope.row.status) }}
        el-table-column(label="重試次數", width="90", align="right")
          template(slot-scope="scope")
            span {{ scope.row.retry_count }}
    .pagination.flex.align-center.justify-end
      el-pagination(
        layout="total, sizes, prev, pager, next, ->, jumper"
        :page-size="limit"
        :current-page="page"
        :total="total"
        :page-sizes="[50, 100, 200]"
        @size-change="changeLimit"
        @current-change="changePage")
  .empty(v-else-if="hasSearched") 此篩選無任何寄送紀錄
  .empty(v-else) 尚未查詢，請設定條件後點選「篩選」
</template>

<script>
import { getEmailLogs, getEmailLogCatalog } from '@/api/log';

const STATUS_LABELS = {
  pending: '等待寄送',
  sent: '已寄送',
  failed: '寄送失敗',
  skipped: '已略過',
};

// 對應後端 admin/services/mailer.py 的 EMAIL_TYPES，
// 後端新增寄信點時這裡補上即可，未對應到的直接顯示原始值
const EMAIL_TYPE_LABELS = {
  user_account_created: '使用者帳號建立',
  user_account_register_finished: '使用者註冊完成',
  user_reset_password: '使用者重設密碼',
  user_password_has_been_reset: '使用者密碼已重設',
  staff_account_created: '員工帳號建立',
  professional_account_created: '專業人士帳號建立',
  reset_password: '重設密碼',
  appointment_created: '預約成立',
  appointment_created_professional: '預約成立（專業人士）',
  appointment_updated: '預約異動',
  appointment_updated_professional: '預約異動（專業人士）',
  appointment_cancelled: '預約取消',
  appointment_cancelled_professional: '預約取消（專業人士）',
  appointment_coming_reminder: '預約提醒',
  appointment_not_paid_reminder: '未付款提醒',
  appointment_paid: '付款完成',
  receipt: '收據',
  professional_unavailable_changed: '專業人士不可排案時段異動',
};

const REFERENCE_TYPE_LABELS = {
  user: '個案',
  staff: '員工',
  professional: '專業人士',
  appointment: '預約',
};

export default {
  data() {
    return {
      catalog: {
        email_types: [],
        statuses: [],
      },
      recipient: '',
      times: [],
      emailTypes: [],
      statuses: [],
      logs: [],
      total: 0,
      page: 1, // el-pagination 為 1 起算，送出前轉成後端的 0 起算
      limit: 50,
      hasSearched: false,
    };
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || status || '';
    },
    statusClass(status) {
      if (status === 'failed') return 'danger-color';
      if (status === 'pending') return 'warn-color';
      if (status === 'skipped') return 'text-placeholder';
      return 'success-color';
    },
    emailTypeLabel(type) {
      return EMAIL_TYPE_LABELS[type] || type || '';
    },
    referenceText(row) {
      if (!row.reference_type && !row.reference_id) return '—';
      const type = REFERENCE_TYPE_LABELS[row.reference_type] || row.reference_type || '';
      return `${type} #${row.reference_id || ''}`;
    },
    buildParams() {
      const params = {
        page: this.page - 1,
        limit: this.limit,
      };
      const recipient = (this.recipient || '').trim();
      if (recipient) {
        params.recipient = recipient;
      }
      if (this.emailTypes.length > 0) {
        params.email_type = this.emailTypes.join(',');
      }
      if (this.statuses.length > 0) {
        params.status = this.statuses.join(',');
      }
      if (this.times && this.times.length === 2) {
        const [start, end] = this.times;
        const startAt = new Date(start);
        startAt.setHours(0, 0, 0, 0);
        const endAt = new Date(end);
        endAt.setHours(23, 59, 59, 999);
        params.start_at = this.$dateToTimestamp(startAt);
        params.end_at = this.$dateToTimestamp(endAt);
      }
      return params;
    },
    search() {
      this.page = 1;
      this.loadLogs();
    },
    changePage(page) {
      this.page = page;
      this.loadLogs();
    },
    changeLimit(limit) {
      this.limit = limit;
      this.page = 1;
      this.loadLogs();
    },
    resetFilter() {
      this.recipient = '';
      this.times = [];
      this.emailTypes = [];
      this.statuses = [];
      this.page = 1;
      this.logs = [];
      this.total = 0;
      this.hasSearched = false;
    },
    loadLogs() {
      this.$execWithLoading(async () => {
        const rsp = await getEmailLogs(this.buildParams());
        this.logs = rsp.email_logs || [];
        this.total = (rsp.meta && rsp.meta.total) || 0;
        this.hasSearched = true;
      }, (e) => {
        this.$showAxiosException('查詢寄送紀錄失敗', e);
      });
    },
    async loadCatalog() {
      try {
        const c = await getEmailLogCatalog();
        if (c) {
          this.catalog = {
            email_types: c.email_types || [],
            statuses: c.statuses || [],
          };
        }
      } catch (e) {
        // catalog 失敗時篩選下拉為空，但收件者與時間區間仍可正常查詢
        this.catalog = { email_types: [], statuses: [] };
      }
    },
  },
  mounted() {
    this.loadCatalog();
  },
};
</script>

<style lang="scss">
@import '@/assets/style/variables.scss';
.mail-record-page {
  .title {
    color: $text-secondary-color;
  }
  .wrap {
    flex-wrap: wrap;
  }
  .empty {
    padding: 60px 0;
    text-align: center;
    color: $text-secondary-color;
  }
  .detail {
    padding: 8px 16px;
    color: $text-regular-color;
    word-break: break-all;
  }
  & > .table {
    overflow: hidden;
  }

  // hack
  .el-table {
    border: 1px solid #EBEEF5;
    border-bottom: none;
    border-left: none;
    display: flex;
    flex-direction: column;

    // handle scroll
    .el-table__header-wrapper {
      flex: 0 0 auto;
    }
    .el-table__body-wrapper {
      flex: 1;
      overflow: overlay;
    }
  }
}
</style>
